import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { getLabelsByInstituteType, MenuProps } from '../../../utils/constants';
const ScheduleFilters = ({
  departments = [],
  onFiltersChange,
  onSearch,
  onClearFilters,
  instituteType,
}) => {
  const { Department, Class, Section } =
    getLabelsByInstituteType(instituteType);
  // Internal filter state
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [filterClass, setFilterClass] = useState('All');
  const [filterSection, setFilterSection] = useState('All');
  const [availableClasses, setAvailableClasses] = useState(['All']);
  const [availableSections, setAvailableSections] = useState(['All']);

  // Prepare department options
  const departmentOptions = [
    'All',
    ...departments.map((d) => d.department || d),
  ];

  // Update classes when department changes
  useEffect(() => {
    if (filterDepartment === 'All') {
      // Show all classes from all departments
      const allClasses = departments.flatMap((d) => d.classes || []);
      const uniqueClasses = [
        ...new Set(allClasses.map((c) => c.className || c)),
      ];
      setAvailableClasses(['All', ...uniqueClasses]);
    } else {
      // Show classes for selected department only
      const dept = departments.find(
        (d) => (d.department || d) === filterDepartment,
      );
      const deptClasses = dept?.classes || [];
      setAvailableClasses(['All', ...deptClasses.map((c) => c.className || c)]);
    }

    // Reset class filter when department changes
    if (filterClass !== 'All') {
      setFilterClass('All');
    }
  }, [filterDepartment, departments]);

  // Update sections when class changes
  useEffect(() => {
    if (filterClass === 'All' || filterDepartment === 'All') {
      setAvailableSections(['All']);
    } else {
      // Find the selected class and get its sections
      const dept = departments.find(
        (d) => (d.department || d) === filterDepartment,
      );
      const selectedClass = dept?.classes?.find(
        (c) => (c.className || c) === filterClass,
      );
      const classSections = selectedClass?.sections || [];
      setAvailableSections(['All', ...classSections]);
    }

    // Reset section filter when class changes
    if (filterSection !== 'All') {
      setFilterSection('All');
    }
  }, [filterClass, filterDepartment, departments]);

  // Notify parent component when filters change
  useEffect(() => {
    const filterData = {
      department: filterDepartment,
      class: filterClass,
      section: filterSection,
    };
    onFiltersChange?.(filterData);
  }, [filterDepartment, filterClass, filterSection, onFiltersChange]);

  const handleDepartmentChange = (value) => {
    setFilterDepartment(value);
  };

  const handleClassChange = (value) => {
    setFilterClass(value);
  };

  const handleSectionChange = (value) => {
    setFilterSection(value);
  };

  const handleSearch = () => {
    onSearch?.({
      department: filterDepartment,
      class: filterClass,
      section: filterSection,
    });
  };

  const handleClearFilters = () => {
    setFilterDepartment('All');
    setFilterClass('All');
    setFilterSection('All');
    onClearFilters?.();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth size='small'>
          <InputLabel>{Department}</InputLabel>
          <Select
            MenuProps={MenuProps}
            value={filterDepartment}
            label={Department}
            onChange={(e) => handleDepartmentChange(e.target.value)}
          >
            {departmentOptions.map((d) => (
              <MenuItem key={d} value={d}>
                {d === 'All' ? `All ${Department}` : d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3}>
        <FormControl fullWidth size='small'>
          <InputLabel>{Class}</InputLabel>
          <Select
            MenuProps={MenuProps}
            value={filterClass}
            label={Class}
            onChange={(e) => handleClassChange(e.target.value)}
          >
            {availableClasses.map((c) => (
              <MenuItem key={c} value={c}>
                {c === 'All' ? `All ${Class}` : c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {instituteType?.toLowerCase() === 'college' && (
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size='small'>
            <InputLabel>{Section}</InputLabel>
            <Select
              MenuProps={MenuProps}
              value={filterSection}
              label={Section}
              onChange={(e) => handleSectionChange(e.target.value)}
            >
              {availableSections.map((s) => (
                <MenuItem key={s} value={s}>
                  {s === 'All' ? `All ${Section}` : s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        md={instituteType?.toLowerCase() === 'college' ? 2 : 3}
        className='flex items-center'
      >
        <Button
          variant='contained'
          fullWidth
          size='medium'
          onClick={handleSearch}
        >
          Search
        </Button>
      </Grid>

      <Grid
        item
        xs={12}
        md={instituteType?.toLowerCase() === 'college' ? 2 : 3}
        className='flex items-center'
      >
        <Button
          variant='outlined'
          color='error'
          fullWidth
          size='medium'
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
      </Grid>
    </Grid>
  );
};

export default ScheduleFilters;
